/**
 * Report Types and Interfaces
 *
 * Type definitions for the Reports page feature.
 * These types are separate from the Forecast DepartmentKey to allow
 * for Reports-specific department handling (no 'ALL' option).
 */

/**
 * Department keys for reports - same as Forecast but without 'ALL'
 */
export type ReportDepartmentKey =
  | 'NEW_VEHICLE'
  | 'USED_VEHICLE'
  | 'SERVICE'
  | 'PARTS'
  | 'BODY_SHOP'
  | 'RENTAL'
  | 'OTHERS'
  | 'MARKETING'
  | 'EXECUTIVE';

/**
 * Report definition - describes a single report type
 */
export interface ReportDefinition {
  /** Unique report identifier */
  id: string;
  /** Display title shown in report list */
  title: string;
  /** Short description shown below title */
  description: string;
  /** Which department tab this report belongs to */
  department: ReportDepartmentKey;
  /** If specified, only these locations can run this report */
  availableForLocations?: number[];
  /** If specified, these locations cannot run this report */
  excludedLocations?: number[];
  /** If true, report is not yet implemented — shown greyed out with "Coming Soon" badge */
  comingSoon?: boolean;
  /** If set, only users with one of these roles can see the report */
  allowedRoles?: string[];
  /** If true, opens a custom multi-location modal instead of the standard ReportModal */
  multiLocation?: boolean;
  /** If true, show a single date picker instead of date range */
  singleDate?: boolean;
  /** If true, report is only available in consolidated view (locationId=0) */
  consolidatedOnly?: boolean;
  /**
   * If true, report runs in BOTH the consolidated view (locationId=0) AND
   * any individual location. Used for reports that the backend filters by
   * locationId — e.g. the CEO Daily Briefing, where a GM can run it scoped
   * to their rooftop while executives still see the all-locations rollup.
   */
  allowsConsolidated?: boolean;
  /**
   * If set, this tighter role list is enforced at the Consolidated view
   * (locationId=0) instead of `allowedRoles`. At individual locations
   * the `allowedRoles` check still applies. Used to let a wider role
   * run a report per-location while restricting the all-locations
   * rollup to a smaller set (e.g. GM excluded from Consolidated brief).
   */
  consolidatedAllowedRoles?: string[];
}

/**
 * Check if a report is available for a given location
 */
export function isReportAvailableForLocation(
  report: ReportDefinition,
  locationId: number,
  userRole?: string | null
): boolean {
  // Coming soon reports are never available
  if (report.comingSoon) {
    return false;
  }
  // TeamServiceManager gets a strict allowlist: a report is visible to a TSM ONLY
  // if it explicitly names TeamServiceManager in allowedRoles. This keeps reports
  // that define no allowedRoles (e.g. service-gross-report) hidden from TSMs, and
  // is future-proof against new un-gated reports leaking onto the TSM Service tab.
  if (userRole === 'TeamServiceManager'
      && !(report.allowedRoles && report.allowedRoles.includes('TeamServiceManager'))) {
    return false;
  }
  // Role check applies in every branch
  if (report.allowedRoles && (!userRole || !report.allowedRoles.includes(userRole))) {
    return false;
  }
  if (locationId === 0) {
    // Consolidated view: only reports that opt into it (either consolidated-only
    // OR dual-mode allowsConsolidated) are available
    if (!report.consolidatedOnly && !report.allowsConsolidated) {
      return false;
    }
    // Tighter role gate at Consolidated, if specified (e.g. GM can run the
    // brief per-location but the all-locations rollup is execs only)
    if (report.consolidatedAllowedRoles
        && (!userRole || !report.consolidatedAllowedRoles.includes(userRole))) {
      return false;
    }
    return true;
  }
  // Individual location view: consolidated-only reports are hidden
  if (report.consolidatedOnly) {
    return false;
  }
  // If whitelist specified, location must be in it
  if (report.availableForLocations && !report.availableForLocations.includes(locationId)) {
    return false;
  }
  // If blacklist specified, location must not be in it
  if (report.excludedLocations && report.excludedLocations.includes(locationId)) {
    return false;
  }
  return true;
}

/**
 * Report execution context - passed when running a report
 */
export interface ReportContext {
  locationId: number;
  locationName: string;
  department: ReportDepartmentKey;
  startDate: string; // ISO date string
  endDate: string;   // ISO date string
}

/**
 * Modal state for report viewing
 */
export interface ReportModalState {
  isOpen: boolean;
  report: ReportDefinition | null;
}
